import {memoize} from 'lodash';
import {rgb} from 'polished';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {tryParseColor} from '../../../../../libs/shared/core/src/models/helpers/colorHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {lightTheme} from '../../../../../libs/shared/ui/src/theme/light/lightTheme';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {FormattingColorPickerDropdown} from './formattingColorPickerDropdown';

/*
 * Props.
 */

interface FormattingTextColorProps {
  textColor?: string;
  onTextColorSelect: (arg: string) => void;
  isDisabled?: boolean;
  isCompact: boolean;
  isInFloatingToolbar: boolean;
}

/*
 * Style.
 */

const StyledIcon = styled(Icon)<{$lineColor: string | undefined}>`
  .color-line {
    color: ${(p) => p.$lineColor || 'currentColor'};
  }
`;

const StyledIconButton = styled(IconButton)<{$lineColor: string | undefined}>`
  .color-line {
    color: ${(p) => p.$lineColor || 'currentColor'};
  }
`;

/*
 * Component.
 */

const label = <Trans id="formatting-textcolor">Text color</Trans>;

export const FormattingTextColor: FC<FormattingTextColorProps> = (props) => {
  const theme = useUserTheme();

  if (props.isCompact) {
    return (
      <PopoverCoordinator
        renderButton={(isOpen) => (
          <StyledIconButton
            isIconInline
            $lineColor={colorToPalette(props.textColor)}
            iconName="formattingColor"
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
            isOpen={isOpen}
            hasHover={isOpen}
            label={label}
            shouldPreventMouseDown={props.isInFloatingToolbar}
          />
        )}
        renderModal={(modalProps) => renderDropdown(modalProps, props, theme)}
      />
    );
  }

  return (
    <DropdownBoxCoordinator
      value={renderValue(props.textColor)}
      size={VisualSizesEnum.MEDIUM}
      isDisabled={props.isDisabled}
      shouldDisablePadding
      renderDropdown={(modalProps) => renderDropdown(modalProps, props, theme)}
      renderTooltip={() => label}
      shouldPreventMouseDown
    />
  );
};

/*
 * Value.
 */

function renderValue(textColor?: string) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledIcon
          $lineColor={colorToPalette(textColor)}
          isInline
          name="formattingColorSmall"
          color={theme.greys.shade80}
        />
      )}
    </UserThemeConsumer>
  );
}

/*
 * Dropdown.
 */

const findColors = memoize((theme: UserTheme) => [
  [
    {color: lightTheme.palette.blue.shade40, lightForeground: true},
    {color: lightTheme.palette.teal.shade40, lightForeground: true},
    {color: lightTheme.palette.green.shade40, lightForeground: true},
    {color: lightTheme.palette.yellow.shade40, lightForeground: true},
    {color: lightTheme.palette.orange.shade40, lightForeground: true},
  ],
  [
    {color: lightTheme.palette.red.shade40, lightForeground: true},
    {color: lightTheme.palette.pink.shade40, lightForeground: true},
    {color: lightTheme.palette.purple.shade40, lightForeground: true},
    {color: lightTheme.greys.shade90, lightForeground: true},
    {color: '#fff', borderColor: theme.greys.shade40, selectedBorderColor: theme.greys.shade70},
  ],
]);

function renderDropdown(modalProps: PopoverTransferProps, props: FormattingTextColorProps, theme: UserTheme) {
  const onTextColorSelect = modalProps.wrap(props.onTextColorSelect);

  return (
    <FormattingColorPickerDropdown
      dropdownProps={modalProps}
      colors={findColors(theme)}
      selectedColor={colorToPalette(props.textColor)}
      onColorSelect={(color) => onTextColorSelect(colorFromPalette(color))}
    />
  );
}

function colorToPalette(color: string | undefined) {
  if (!color) {
    return undefined;
  }

  const parsedColor = tryParseColor(color);
  if (!parsedColor) {
    return undefined;
  }

  const hexColor = rgb(parsedColor);

  if (hexColor === '#000') {
    return lightTheme.greys.shade90;
  }

  return hexColor;
}

function colorFromPalette(color: string | undefined) {
  if (!color || color === lightTheme.greys.shade90) {
    return '#000';
  }

  return color;
}
